import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, X } from 'lucide-react';

/**
 * WhyThisPopover - Shows why items were recommended with score breakdown
 * 
 * Props:
 * - scoreBreakdown: { vector, tags, colors, recency, personalization? } (from item)
 * - biasUsed: { tags: Record<string, number>, colors: Record<string, number> } (from metadata)
 * - biasContribution: { tags: string[], colors: string[] } (from item)
 * - matchReasons: string[] (from item)
 * - item: ItemDTO (optional, for item-specific popover)
 */
export default function WhyThisPopover({
  scoreBreakdown,
  biasUsed,
  biasContribution,
  matchReasons,
  item,
}) {
  // Use item's scoreBreakdown if item is provided
  const effectiveScoreBreakdown = item?.scoreBreakdown || scoreBreakdown;
  const effectiveMatchReasons = item?.matchReasons || matchReasons;
  const effectiveBiasContribution = item?.biasContribution || biasContribution;
  const [isOpen, setIsOpen] = useState(false);
  const [showDelay, setShowDelay] = useState(null);
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    return () => {
      if (showDelay) {
        clearTimeout(showDelay);
      }
    };
  }, [showDelay]);

  const handleMouseEnter = () => {
    const delay = setTimeout(() => {
      setIsOpen(true);
    }, 300); // 300ms delay
    setShowDelay(delay);
  };

  const handleMouseLeave = () => {
    if (showDelay) {
      clearTimeout(showDelay);
      setShowDelay(null);
    }
    setIsOpen(false);
  };

  const handleEscape = (e) => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Focus trap
      if (popoverRef.current) {
        const focusableElements = popoverRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!effectiveScoreBreakdown && !effectiveBiasContribution && !effectiveMatchReasons && !biasUsed) {
    return null;
  }

  const reasons = [];
  
  if (effectiveMatchReasons) {
    if (effectiveMatchReasons.includes('semantic-match')) {
      reasons.push({
        type: 'semantic',
        label: 'Semantic match',
        score: effectiveScoreBreakdown?.vector,
      });
    }
    if (effectiveMatchReasons.includes('tag-match')) {
      reasons.push({
        type: 'tag',
        label: 'Tag match',
        score: effectiveScoreBreakdown?.tags,
      });
    }
    if (effectiveMatchReasons.includes('color-match')) {
      reasons.push({
        type: 'color',
        label: 'Color match',
        score: effectiveScoreBreakdown?.colors,
      });
    }
    if (effectiveMatchReasons.includes('personalized')) {
      reasons.push({
        type: 'personalized',
        label: 'Personalized for you',
        score: effectiveScoreBreakdown?.personalization,
      });
    }
    if (effectiveMatchReasons.includes('recent')) {
      reasons.push({
        type: 'recent',
        label: 'Recently added',
        score: effectiveScoreBreakdown?.recency,
      });
    }
    if (effectiveMatchReasons.includes('trending-tags')) {
      reasons.push({
        type: 'trending',
        label: 'Trending tags',
        score: effectiveScoreBreakdown?.tags,
      });
    }
    if (effectiveMatchReasons.includes('trending-colors')) {
      reasons.push({
        type: 'trending',
        label: 'Trending colors',
        score: effectiveScoreBreakdown?.colors,
      });
    }
  }

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => setIsOpen(!isOpen)}
        className="p-1.5 text-neutral-500 hover:text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors"
        aria-label="Why this recommendation?"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Info className="w-4 h-4" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={popoverRef}
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
            className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-neutral-200 z-50 p-4"
            role="dialog"
            aria-labelledby="why-this-title"
            aria-modal="false"
          >
            <div className="flex items-start justify-between mb-3">
              <h3 id="why-this-title" className="text-sm font-semibold text-neutral-900">
                Why this recommendation?
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-neutral-400 hover:text-neutral-600 rounded"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Score Breakdown */}
            {effectiveScoreBreakdown && (
              <div className="mb-4">
                <h4 className="text-xs font-medium text-neutral-700 mb-2">Score Breakdown</h4>
                <div className="space-y-1.5">
                  {effectiveScoreBreakdown.vector !== undefined && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-neutral-600">Semantic similarity</span>
                      <span className="font-medium text-neutral-900">
                        {Math.round(effectiveScoreBreakdown.vector * 100)}%
                      </span>
                    </div>
                  )}
                  {effectiveScoreBreakdown.tags !== undefined && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-neutral-600">Tag match</span>
                      <span className="font-medium text-neutral-900">
                        {Math.round(effectiveScoreBreakdown.tags * 100)}%
                      </span>
                    </div>
                  )}
                  {effectiveScoreBreakdown.colors !== undefined && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-neutral-600">Color match</span>
                      <span className="font-medium text-neutral-900">
                        {Math.round(effectiveScoreBreakdown.colors * 100)}%
                      </span>
                    </div>
                  )}
                  {effectiveScoreBreakdown.recency !== undefined && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-neutral-600">Recency</span>
                      <span className="font-medium text-neutral-900">
                        {Math.round(effectiveScoreBreakdown.recency * 100)}%
                      </span>
                    </div>
                  )}
                  {effectiveScoreBreakdown.personalization !== undefined && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-neutral-600">Personalization</span>
                      <span className="font-medium text-[#7A47F5]">
                        {Math.round(effectiveScoreBreakdown.personalization * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bias Used (from metadata) */}
            {biasUsed && (
              <div className="mb-4">
                <h4 className="text-xs font-medium text-neutral-700 mb-2">Based on your preferences</h4>
                {biasUsed.tags && Object.keys(biasUsed.tags).length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs text-neutral-600 mb-1">Top tags:</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(biasUsed.tags)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 5)
                        .map(([tag], idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded"
                          >
                            {tag}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
                {biasUsed.colors && Object.keys(biasUsed.colors).length > 0 && (
                  <div>
                    <p className="text-xs text-neutral-600 mb-1">Top colors:</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(biasUsed.colors)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 3)
                        .map(([color], idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded flex items-center gap-1"
                          >
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                            {color}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bias Contribution (from item) */}
            {effectiveBiasContribution && (effectiveBiasContribution.tags?.length > 0 || effectiveBiasContribution.colors?.length > 0) && (
              <div className="mb-4">
                <h4 className="text-xs font-medium text-neutral-700 mb-2">This item matches</h4>
                {effectiveBiasContribution.tags?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs text-neutral-600 mb-1">Tags:</p>
                    <div className="flex flex-wrap gap-1">
                      {effectiveBiasContribution.tags.slice(0, 5).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {effectiveBiasContribution.colors?.length > 0 && (
                  <div>
                    <p className="text-xs text-neutral-600 mb-1">Colors:</p>
                    <div className="flex flex-wrap gap-1">
                      {effectiveBiasContribution.colors.slice(0, 3).map((color, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded flex items-center gap-1"
                        >
                          <span
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: color }}
                          />
                          {color}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Match Reasons */}
            {reasons.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-neutral-700 mb-2">Match Reasons</h4>
                <div className="flex flex-wrap gap-1">
                  {reasons.map((reason, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded"
                    >
                      {reason.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

